import type {
  AIActionKey,
  CustomerActionAllowanceSnapshot,
  FeatureKey,
  SubscriptionCatalog,
  SubscriptionSnapshot,
} from '@tabbio/types';

/**
 * @deprecated Legacy surface for the pre-redesign cancel sheet/dialog and the
 * old billing value panel (`CancelPlanSheet.tsx`, `CancelPlanDialog.web.tsx`,
 * `SubscriptionValue(.web).tsx`, the current `BillingPanel`). The journey logic
 * moved to `lib/subscription-journey/cancel-journey.ts`; the still-valid helpers
 * are re-exported from there so both surfaces share one implementation.
 * Delete this file together with those surfaces (subscription journeys
 * Phase 2/3).
 */
export {
  canStartCancellation,
  formatCancelAccessDate,
  getCancelAccessDate,
  getCancelAnalyticsProperties,
  getCancelDestination,
  getCancellationScope,
  type CancelDestination,
} from './subscription-journey/cancel-journey';

// Billing providers own cancellation. This model only describes the review
// before the handoff; a click here never means a subscription was cancelled.
/** @deprecated The old single-sheet reasons. The journey uses `CANCEL_REASON_LIST`. */
export const CANCEL_REASONS = [
  'not_needed_now',
  'too_expensive',
  'hard_to_use',
  'missing_feature',
  'quality_issue',
  'switching',
  'other',
] as const;
/** @deprecated See `CANCEL_REASONS`. */
export type CancelReason = (typeof CANCEL_REASONS)[number];

export const VALUE_ACTIONS = {
  'individual.career_agent_chat': 'chat',
  'individual.skill_run': 'skills',
  'individual.cv_section_preview': 'cvPreviews',
  cv_fit_report_generate: 'fitReports',
  'individual.nova_compile': 'taskPreviews',
} as const satisfies Partial<Record<AIActionKey, string>>;

export type ValueAllowance = CustomerActionAllowanceSnapshot & {
  label: string;
};

export function getValueAllowances(
  snapshot: SubscriptionSnapshot | null,
  now = Date.now(),
): ValueAllowance[] {
  // Use only server usage, never catalog estimates, credits, in-flight
  // reservations, or sums of activities with different reset windows.
  return Object.entries(VALUE_ACTIONS).flatMap(([key, label]) => {
    const item = snapshot?.actionAllowances?.[key as AIActionKey];
    if (
      !item ||
      item.actionKey !== key ||
      !['day', 'week', 'month'].includes(item.cadence) ||
      !Number.isInteger(item.used) ||
      item.used < 0 ||
      !Number.isInteger(item.remaining) ||
      item.remaining < 0 ||
      !Number.isInteger(item.limit) ||
      item.limit <= 0 ||
      !(Date.parse(item.resetAt) > now)
    )
      return [];
    return [{ ...item, label }];
  });
}

const BENEFITS: { key: string; feature: FeatureKey }[] = [
  { key: 'cv', feature: 'individual.cv_writer' },
  { key: 'automation', feature: 'individual.automations' },
  { key: 'interview', feature: 'individual.interview_prep' },
  { key: 'letters', feature: 'individual.cover_letter' },
  { key: 'analytics', feature: 'individual.sources_analytics' },
  { key: 'ranking', feature: 'employer.candidate_ranking' },
  { key: 'screening', feature: 'employer.bulk_screening' },
  { key: 'outreach', feature: 'employer.outreach' },
  { key: 'team', feature: 'employer.team_analytics' },
];

export function getPlanBenefits(
  catalog: SubscriptionCatalog,
  snapshot: SubscriptionSnapshot | null,
): string[] {
  if (!snapshot || snapshot.tier === 'free') return [];
  const features =
    catalog.planSets[snapshot.planSet]?.tiers[snapshot.tier]?.features ?? [];
  return BENEFITS.filter(({ feature }) => features.includes(feature))
    .slice(0, 3)
    .map(({ key }) => key);
}
